import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, AlertCircle, CheckCircle2, XCircle, Edit3 } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth } from "@/lib/auth-client";
import type { CreateVendorRequest, VendorDto } from "@procureflow/shared-types";

export const Route = createFileRoute("/vendors/$id")({
  head: () => ({
    meta: [
      { title: "Vendor — ProcureFlow" },
      { name: "description", content: "Vendor detail and approval actions." },
    ],
  }),
  component: VendorDetail,
});

function statusBadgeClasses(status: VendorDto["status"]): string {
  switch (status) {
    case "Active":          return "[color:var(--success)] bg-success/10 border-success/20";
    case "PendingApproval": return "[color:var(--warning)] bg-warning/10 border-warning/20";
    case "Rejected":        return "text-destructive bg-destructive/10 border-destructive/20";
  }
}

function statusLabel(status: VendorDto["status"]): string {
  return status === "PendingApproval" ? "Pending Approval" : status;
}

const EDITABLE_FIELDS: Array<{ key: keyof CreateVendorRequest; label: string }> = [
  { key: "company_name",  label: "Company Name" },
  { key: "address",       label: "Address" },
  { key: "contact_name",  label: "Contact Name" },
  { key: "contact_email", label: "Contact Email" },
  { key: "contact_phone", label: "Contact Phone" },
  { key: "bank_details",  label: "Bank Details" },
  { key: "tax_id",        label: "Tax ID" },
];

function VendorDetail() {
  const { id } = useParams({ from: "/vendors/$id" });
  const { user } = useAuth();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState<VendorDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<CreateVendorRequest>({
    company_name: "", address: "", contact_name: "", contact_email: "",
    contact_phone: "", bank_details: "", tax_id: "",
  });

  const isAdmin = user?.role === "Admin";
  const canEditRejected = user?.role === "ProcurementOfficer" || user?.role === "Admin";

  async function fetchVendor() {
    if (import.meta.env.DEV) {
      const { mswReady } = await import("@/mocks/browser");
      await mswReady;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`/api/vendors/${id}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.message ?? "Failed to load vendor.");
        return;
      }
      const { data } = (await res.json()) as { data: VendorDto };
      setVendor(data);
      setEditForm({
        company_name:   data.company_name,
        address:        data.address,
        contact_name:   data.contact_name,
        contact_email:  data.contact_email,
        contact_phone:  data.contact_phone,
        bank_details:   data.bank_details,
        tax_id:         data.tax_id,
      });
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchVendor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleApprove() {
    if (!vendor) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await fetchWithAuth(`/api/vendors/${vendor.id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setActionError(body.message ?? "Failed to approve vendor.");
        return;
      }
      const { data } = (await res.json()) as { data: VendorDto };
      setVendor(data);
    } catch {
      setActionError("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReject(e: React.FormEvent) {
    e.preventDefault();
    if (!vendor) return;
    if (!rejectReason.trim()) {
      setActionError("Rejection reason is required.");
      return;
    }
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await fetchWithAuth(`/api/vendors/${vendor.id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comment: rejectReason }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setActionError(body.message ?? "Failed to reject vendor.");
        return;
      }
      const { data } = (await res.json()) as { data: VendorDto };
      setVendor(data);
      setRejectMode(false);
      setRejectReason("");
    } catch {
      setActionError("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleEditSave(e: React.FormEvent) {
    e.preventDefault();
    if (!vendor) return;
    const missing = EDITABLE_FIELDS.filter(f => !editForm[f.key].trim()).map(f => f.label);
    if (missing.length > 0) {
      setActionError(`Missing required fields: ${missing.join(", ")}.`);
      return;
    }
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await fetchWithAuth(`/api/vendors/${vendor.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setActionError(body.message ?? "Failed to update vendor.");
        return;
      }
      const { data } = (await res.json()) as { data: VendorDto };
      setVendor(data);
      setEditMode(false);
    } catch {
      setActionError("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center">
        <p className="text-sm text-destructive">{error ?? "Vendor not found."}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <button
          onClick={() => navigate({ to: "/vendors" })}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Back to vendors
        </button>
        <div className="flex items-start justify-between gap-4 mt-2">
          <div>
            <h1 className="text-lg font-semibold tracking-tight">{vendor.company_name}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{vendor.address}</p>
          </div>
          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${statusBadgeClasses(vendor.status)}`}>
            {statusLabel(vendor.status)}
          </span>
        </div>
      </div>

      {actionError && (
        <div className="flex items-start gap-2.5 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          {actionError}
        </div>
      )}

      {vendor.status === "Rejected" && vendor.rejection_reason && !editMode && (
        <div className="rounded-2xl bg-destructive/5 border border-destructive/20 p-5 space-y-2">
          <p className="text-sm font-medium text-destructive">Rejection reason</p>
          <p className="text-sm text-foreground">{vendor.rejection_reason}</p>
        </div>
      )}

      {editMode ? (
        <form onSubmit={handleEditSave} className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-4" noValidate>
          <h2 className="text-sm font-semibold">Edit vendor</h2>
          {EDITABLE_FIELDS.map(field => (
            <label key={field.key} className="block space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">{field.label}</span>
              <input
                type="text"
                value={editForm[field.key]}
                onChange={e => setEditForm(s => ({ ...s, [field.key]: e.target.value }))}
                aria-label={field.label}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
          ))}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => { setEditMode(false); setActionError(null); }}
              className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted/40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-glow hover:opacity-95 disabled:opacity-60"
            >
              {submitting ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      ) : (
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-4">
          <h2 className="text-sm font-semibold">Vendor details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Contact</p>
              <p className="mt-1">{vendor.contact_name}</p>
              <p className="text-xs text-muted-foreground">{vendor.contact_email}</p>
              <p className="text-xs text-muted-foreground">{vendor.contact_phone}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Tax ID</p>
              <p className="mt-1 font-mono">{vendor.tax_id}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs text-muted-foreground">Bank Details</p>
              <p className="mt-1 font-mono">{vendor.bank_details}</p>
            </div>
          </div>
        </div>
      )}

      {/* Action bar */}
      {!editMode && (
        <div className="flex flex-wrap gap-2">
          {isAdmin && vendor.status === "PendingApproval" && !rejectMode && (
            <>
              <button
                onClick={handleApprove}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-3.5 py-2 text-sm font-medium text-primary-foreground shadow-glow hover:opacity-95 disabled:opacity-60"
              >
                <CheckCircle2 className="h-4 w-4" /> Approve
              </button>
              <button
                onClick={() => { setRejectMode(true); setActionError(null); }}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-lg border border-destructive/40 text-destructive px-3.5 py-2 text-sm font-medium hover:bg-destructive/10"
              >
                <XCircle className="h-4 w-4" /> Reject
              </button>
            </>
          )}
          {canEditRejected && vendor.status === "Rejected" && (
            <button
              onClick={() => { setEditMode(true); setActionError(null); }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3.5 py-2 text-sm font-medium hover:bg-muted/40"
            >
              <Edit3 className="h-4 w-4" /> Edit
            </button>
          )}
        </div>
      )}

      {rejectMode && (
        <form onSubmit={handleReject} className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-3">
          <h2 className="text-sm font-semibold">Reject vendor</h2>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Reason (required)</span>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              aria-label="Reason"
              rows={3}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setRejectMode(false); setRejectReason(""); setActionError(null); }}
              className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted/40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded-lg border border-destructive/40 text-destructive bg-destructive/5 px-4 py-2 text-sm font-medium hover:bg-destructive/10 disabled:opacity-60"
            >
              Submit
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
